// Import Dependencies
import { Navigate } from "react-router";

// Local Imports
import { AppLayout } from "app/layouts/AppLayout";
import { DynamicLayout } from "app/layouts/DynamicLayout";
import AuthGuard from "middleware/AuthGuard";

// ----------------------------------------------------------------------

const protectedRoutes = {
  id: "protected",
  Component: AuthGuard,
  children: [
    // The dynamic layout supports both the main layout and the sideblock.
    {
      Component: DynamicLayout,
      children: [
        {
          index: true,
          element: <Navigate to="/dashboards" />,
        },
        {
          path: "notifications",
          lazy: async () => ({
            Component: (await import("components/leadUser/stages/NotificationsPage.jsx")).default,
          }),
        },
        {
          path: "no-access",
          lazy: async () => ({
            Component: (await import("app/pages/NoAccess")).default,
          }),
        },
        {
          path: "dashboards",
          children: [
            {
              index: true,
              element: <Navigate to="/dashboards/home" />,
            },
            {
              path: "home",
              lazy: async () => ({
                Component: (await import("app/pages/dashboards/home")).default,
              }),
            },
            {
              path: "councellorDashboard",
              lazy: async () => ({
                Component: (await import("app/pages/dashboards/CouncellorDashboard/councellorDashboardHome")).default,
              }),
            },
          ],
        },
        {
          path: "dashboard",
          children: [
            {
              path: "overview",
              lazy: async () => ({
                Component: (await import("app/pages/dashboard/overview"))
                  .default,
              }),
            },
          ],
        },
        {
          path: "leads",
          children: [
            {
              path: "manager",
              lazy: async () => {
                const Module = (await import("app/pages/leads/manager")).default;

                return {
                  Component: () => <Module key="leads-manager" />,
                };
              },
            }
            ,
            {
              path: "manager/:id/:section?",
              lazy: async () => {
                const Module = (await import("app/pages/leads/manager")).default;

                return {
                  Component: () => <Module key="leads-manager" />,
                };
              },
            },
            {
              path: "details/:id",
              lazy: async () => ({
                Component: (await import("app/pages/leads/details")).default,
              }),
            },
            {
              path: "all",
              lazy: async () => ({
                Component: (await import("app/pages/leads/all")).default,
              }),
            },
            {
              path: "follow-ups",
              lazy: async () => ({
                Component: (await import("app/pages/leads/follow-ups")).default,
              }),
            },

          ],
        },
        {
          path: "query",
          children: [
            {
              index: true,
              element: <Navigate to="/query/manage-applicant-queries" />,
            },
            {
              path: "manage-applicant-queries",
              lazy: async () => ({
                Component: (
                  await import("app/pages/query/ManageApplicantQueries")
                ).default,
              }),
            },
            {
              path: "details/:id",
              lazy: async () => ({
                Component: (await import("app/pages/query/QueryDetails"))
                  .default,
              }),
            },
            {
              path: "manage-faq",
              lazy: async () => ({
                Component: (await import("app/pages/query/ManageFAQ")).default,
              }),
            },
          ],
        },
        {
          path: "forms",
          children: [
            {
              index: true,
              element: <Navigate to="/forms/form-builder" />,
            },
            {
              path: "form-builder",
              lazy: async () => ({
                Component: (await import("app/pages/Builder")).default,
              }),
            },
            {
              path: "master-fields",
              lazy: async () => ({
                Component: (await import("app/pages/MasterFieldsManagement"))
                  .default,
              }),
            },
          ],
        },
        {
          path: "followups",
          children: [
            {
              index: true,
              element: <Navigate to="/followups/calendar" />,
            },
            {
              path: "calendar",
              lazy: async () => ({
                Component: (await import("app/pages/followups")).default,
              }),
            },
          ],
        },
        {
          path: "lead-user",
          children: [
            {
              index: true,
              element: <Navigate to="/lead-user/applications" />,
            },

            {
              path: "applications",
              lazy: async () => ({
                Component: (
                  await import("app/pages/leadUserView/ApplicationListing")
                ).default,
              }),
            },
            {
              path: "applications/:applicationId",
              lazy: async () => ({
                Component: (
                  await import("app/pages/leadUserView/ApplicationDetails")
                ).default,
              }),
            },
            {
              path: "applications/:applicationId/queries",
              lazy: async () => ({
                Component: (
                  await import("app/pages/leadUserView/LeadQueries")
                ).default,
              }),
            },


          ],
        },
        {
          path: "verification",
          children: [
            {
              index: true,
              element: <Navigate to="/verification/document-verification" />,
            },

            {
              path: "document-verification",
              lazy: async () => ({
                Component: (await import("app/pages/verification/DocumentVerification"))
                  .default,
              }),
            },
            {
              path: "document-verification/:leadId",
              lazy: async () => ({
                Component: (await import("app/pages/verification/ViewDocuments"))
                  .default,
              }),
            },

          ],
        },
        {
          path: "application",
          children: [
            {
              index: true,
              element: <Navigate to="/application/listing" />,
            },
            // {
            //   path: "listing",
            //   lazy: async () => ({
            //     Component: (await import("app/pages/Application/Application"))
            //       .default,
            //   }),
            // },

            {
              path: "listing",
              lazy: async () => {
                const Module = (await import("app/pages/leads/manager")).default;

                return {
                  Component: () => <Module key="leads-listing" />
                };
              },
            },

            {
              path: "details/:id",
              lazy: async () => {
                const LeadDetails = (await import("app/pages/leads/details")).default;
                return {
                  Component: () => <LeadDetails entityName="Application" />,
                };
              },
            },
            {
              path: "follow-ups",
              lazy: async () => ({
                Component: (await import("app/pages/leads/follow-ups")).default,
              }),
            },
          ],
        },

        {
          path: "reports",
          children: [
            {
              index: true,
              element: <Navigate to="/reports/total-leads" />,
            },
            {
              path: "total-leads",
              lazy: async () => ({
                Component: (
                  await import("app/pages/reports/TotalLeadsReport")
                ).default,
              }),
            },
            {
              path: "lead-journey",
              lazy: async () => ({
                Component: (
                  await import("app/pages/reports/LeadJourneyReport")
                ).default,
              }),
            },
            {
              path: "lead-source",
              lazy: async () => ({
                Component: (
                  await import("app/pages/reports/LeadSourceReport")
                ).default,
              }),
            },
            {
              path: "lead-course",
              lazy: async () => ({
                Component: (
                  await import("app/pages/reports/LeadsbyCourseReport")
                ).default,
              }),
            },
            {
              path: "followup-status",
              lazy: async () => ({
                Component: (
                  await import("app/pages/reports/FollowupStatusReport")
                ).default,
              }),
            },
            {
              path: "overdue-missed",
              lazy: async () => ({
                Component: (
                  await import("app/pages/reports/OverdueMissedReport")
                ).default,
              }),
            },
            {
              path: "application-status",
              lazy: async () => ({
                Component: (
                  await import("app/pages/reports/ApplicationStatusReport")
                ).default,
              }),
            },
            {
              path: "lead-applicant-conversion",
              lazy: async () => ({
                Component: (
                  await import("app/pages/reports/LeadApplicantConversionReport")
                ).default,
              }),
            },
            {
              path: "counsellor-performance",
              lazy: async () => ({
                Component: (
                  await import("app/pages/reports/CounsellorPerformance")
                ).default,
              }),
            },
            {
              path: "overdue-&-missed",
              lazy: async () => ({
                Component: (
                  await import("app/pages/reports/OverdueMissedReport")
                ).default,
              }),
            },
            {
              path: "payment-link",
              lazy: async () => ({
                Component: (
                  await import("app/pages/reports/PaymentLinkReport")
                ).default,
              }),
            },
            {
              path: "payment-completion",
              lazy: async () => ({
                Component: (
                  await import("app/pages/reports/PaymentCompletionReport")
                ).default,
              }),
            },
            {
              path: "query-status",
              lazy: async () => ({
                Component: (
                  await import("app/pages/reports/QueryStatusReport")
                ).default,
              }),
            },
          ],

        },

      ],
    },
    // The app layout supports only the main layout. Avoid using it for other layouts.
    {
      Component: AppLayout,
      children: [
        {
          path: "settings",
          lazy: async () => ({
            Component: (await import("app/pages/settings/Layout")).default,
          }),
          children: [
            {
              index: true,
              element: <Navigate to="/settings/general" />,
            },
            {
              path: "general",
              lazy: async () => ({
                Component: (
                  await import("app/pages/settings/sections/general/General")
                ).default,
              }),
            },
            {
              path: "appearance",
              lazy: async () => ({
                Component: (
                  await import(
                    "app/pages/settings/sections/appearance/Appearance"
                  )
                ).default,
              }),
            },
            {
              path: "user-management",
              lazy: async () => ({
                Component: (
                  await import(
                    "app/pages/settings/sections/UserManagement/UserManagement"
                  )
                ).default,
              }),
            },
            {
              path: "team-management",
              lazy: async () => ({
                Component: (
                  await import(
                    "app/pages/settings/sections/TeamManagement/TeamManagement"
                  )
                ).default,
              }),
            },
            {
              path: "designation-management",
              lazy: async () => ({
                Component: (
                  await import(
                    "app/pages/settings/sections/DesignationManagement/DesignationManagement"
                  )
                ).default,
              }),
            },
            {
              path: "template-management",
              lazy: async () => ({
                Component: (
                  await import(
                    "app/pages/settings/sections/TemplateManagement/TemplateManagement"
                  )
                ).default,
              }),
            },
            {
              path: "role-permissions",
              children: [
                {
                  index: true,
                  lazy: async () => ({
                    Component: (
                      await import(
                        "app/pages/settings/sections/RolePermissions/RolePermissions"
                      )
                    ).default,
                  }),
                },
                {
                  path: ":roleId",
                  lazy: async () => ({
                    Component: (
                      await import(
                        "app/pages/settings/sections/RolePermissions/RolePermissions"
                      )
                    ).default,
                  }),
                },
              ],
            },
            {
              path: "rule-management",
              lazy: async () => ({
                Component: (await import("app/pages/rules/RuleManagement"))
                  .default,
              }),
            },
            {
              path: "event-score",
              lazy: async () => ({
                Component: (
                  await import(
                    "app/pages/settings/sections/EventScore/EventScore"
                  )
                ).default,
              }),
            },
            {
              path: "integrations",
              lazy: async () => ({
                Component: (
                  await import(
                    "app/pages/settings/sections/integrations/Integrations"
                  )
                ).default,
              }),
            },
            {
              path: "payment-catalogs",
              lazy: async () => ({
                Component: (
                  await import(
                    "app/pages/settings/sections/PaymentCatalogs/PaymentCatalogs"
                  )
                ).default,
              }),
            },
            {
              path: "lead-stages",
              lazy: async () => ({
                Component: (
                  await import(
                    "app/pages/settings/sections/LeadStageManagement/LeadStageManagement"
                  )
                ).default,
              }),
            },
            {
              path: "api-keys",
              lazy: async () => ({
                Component: (
                  await import("app/pages/settings/sections/apiKeys/ApiKeys")
                ).default,
              }),
            },
            {
              path: "branding",
              lazy: async () => ({
                Component: (
                  await import("app/pages/settings/sections/builder/Builder")
                ).default,
              }),
            },

          ],
        },
      ],
    },
  ],
};

export { protectedRoutes };
